import { useSpOnboardingState } from "~/api/v2/useSpOnboardingState";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { CompletionStep } from "../molecules/onboard-sharepoint/CompletionStep";
import { AdminConsentStep } from "~/molecules/onboard-sharepoint/AdminConsentStep";
import { SignInStep } from "~/molecules/onboard-sharepoint/SignInStep";
import { SelectDestinationStep } from "~/molecules/onboard-sharepoint/SelectDestinationStep";
import { OnboardingProgress } from "~/molecules/OnboardingProgress";
import { GrantAccessStep } from "../molecules/onboard-sharepoint/GrantAccessStep";
import { VerifyAccessStep } from "../molecules/onboard-sharepoint/VerifyAccessStep";
import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

const SharePointOnboardingPage = () => {
  const { t } = useTranslation();
  const isSpMirroringEnabled = useFeatureFlagEnabled(featureFlags.spMirroring);
  const { data, isLoading, mutate } = useSpOnboardingState();

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!data || !isSpMirroringEnabled) {
    return <NoResult />;
  }

  const steps = [
    {
      title: t(($) => $.sharepoint.steps.adminConsent.title),
      description: t(($) => $.sharepoint.steps.adminConsent.description),
      isCompleted: data.tenantId !== null,
      component: <AdminConsentStep clientId={data.clientId} />,
    },
    {
      title: t(($) => $.sharepoint.steps.signIn.title),
      description: t(($) => $.sharepoint.steps.signIn.description),
      isCompleted: data.hasSpAccessToken || data.onboardingCompleted === true,
      component: <SignInStep tenantId={data.tenantId} />,
    },
    {
      title: t(($) => $.sharepoint.steps.selectDestination.title),
      description: t(($) => $.sharepoint.steps.selectDestination.description),
      isCompleted: data.siteId !== null,
      component: <SelectDestinationStep onSubmit={mutate} />,
    },
    {
      title: t(($) => $.sharepoint.steps.grantAccess.title),
      description: t(($) => $.sharepoint.steps.grantAccess.description),
      isCompleted: data.permissionId !== null,
      component: <GrantAccessStep onSubmit={mutate} />,
    },
    {
      title: t(($) => $.sharepoint.steps.verifyAccess.title),
      description: t(($) => $.sharepoint.steps.verifyAccess.description),
      isCompleted: data.onboardingCompleted === true,
      component: <VerifyAccessStep onSubmit={mutate} />,
    },
    {
      title: null,
      description: null,
      isCompleted: false,
      component: <CompletionStep />,
    },
  ];

  const currentIndex = steps.findIndex((step) => step.isCompleted === false);

  return (
    <div className="flex flex-col gap-8 py-8">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-3xl font-bold">{t(($) => $.sharepoint.title)}</h1>
        <p className="text-muted-foreground max-w-2xl">
          {t(($) => $.sharepoint.description)}
        </p>
      </div>
      {currentIndex < steps.length - 1 && (
        <OnboardingProgress currentIndex={currentIndex} steps={steps} />
      )}
      <div className="flex justify-center">
        <div className="w-full max-w-2xl">{steps[currentIndex]?.component}</div>
      </div>
    </div>
  );
};

export default SharePointOnboardingPage;
